/* eslint-disable quotes */
/**
 * Converts a subset of markdown to HTML.
 * Handles headers, bold, italics, inline code, links, lists, tables, and paragraphs.
 *
 * @param markdown - The markdown string to convert.
 * @returns The converted HTML string.
 */
export function markdownToHtml(markdown: string): string {
	if (typeof markdown !== 'string') {
		return '';
	}

	// Escape HTML entities
	markdown = markdown
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');

	// Headers: #, ##, ### ...
	markdown = markdown.replace(/^###### (.*$)/gim, '<h6>$1</h6>');
	markdown = markdown.replace(/^##### (.*$)/gim, '<h5>$1</h5>');
	markdown = markdown.replace(/^#### (.*$)/gim, '<h4>$1</h4>');
	markdown = markdown.replace(/^### (.*$)/gim, '<h3>$1</h3>');
	markdown = markdown.replace(/^## (.*$)/gim, '<h2>$1</h2>');
	markdown = markdown.replace(/^# (.*$)/gim, '<h1>$1</h1>');

	// Bold, italic, code, links
	markdown = markdown.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');
	markdown = markdown.replace(/\*(.*?)\*/gim, '<em>$1</em>');
	markdown = markdown.replace(/`([^`]+)`/gim, '<code>$1</code>');
	markdown = markdown.replace(
		/\[([^\]]+)\]\(\s*([^\s)]+)(?:\s+(["'])(.*?)\3)?\s*\)/g,
		(m, text, url, quote, title) =>
			`<a href="${url}">${text}${title ? ` <span class="visually-hidden">&nbsp;${title}</span>` : ""}</a>`,
	);

	markdown = markdown.replace(/(?<!href="mailto:)(?<!<a[^>]*>)(\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})(?![^<]*<\/a>)/g, '<a href="mailto:$1">$1</a>');

	// Tables
	markdown = markdown.replace(
		/((?:\|.*\|\n)+)(?:\|[-:| ]+\|\n)((?:\|.*\|\n?)+)/g,
		(_, header, body) => {
			const parseRow = (row: string, cellTag: 'th' | 'td') =>
				'<tr>' +
				row
					.trim()
					.slice(1, -1)
					.split('|')
					.map(cell => `<${cellTag}>${cell.trim()}</${cellTag}>`)
					.join('') +
				'</tr>';

			const thead = parseRow(header, 'th');
			const tbody = body
				.trim()
				.split('\n')
				.map((row: any) => parseRow(row, 'td'))
				.join('');

			return `<table><thead>${thead}</thead><tbody>${tbody}</tbody></table>`;
		},
	);

	// Step 1: Convert markdown bullets to <li>
	markdown = markdown.replace(/^\s*[-*]\s+(.*)$/gim, '<li>$1</li>');

	// Step 2: Wrap consecutive <li> blocks in <ul>
	markdown = markdown.replace(/(<li>[\s\S]*?<\/li>)(?=(?:\s*<li>|$))/gim, '$1');
	markdown = markdown.replace(/(<li>.*<\/li>\s*)+/gim, '<ul>$&</ul>');


	// Paragraphs
	markdown = markdown.replace(/^\s*(?!<\/?(h[1-6]|ul|li|p|a|code|strong|em|table|thead|tbody|tr|th|td)).+$/gim, '<p>$&</p>');

	return markdown.trim();
}
